import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Layers, Maximize, Minimize, ChevronLeft, ChevronRight, QrCode, MessageCircle } from 'lucide-react';
import { getSession } from '../services/api';
import websocketService from '../services/websocket';
import QRCodeModal from '../components/QRCodeModal';
import PDFSlideViewer from '../components/PDFSlideViewer';
import SwipeUpPanel from '../components/SwipeUpPanel';

const ViewerMain = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [presentation, setPresentation] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isSlideFullscreen, setIsSlideFullscreen] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const fullscreenRef = useRef(null);
  const [showSlideshowControls, setShowSlideshowControls] = useState(true);
  const slideshowControlsTimer = useRef(null);
  const [showSpeakerNotes, setShowSpeakerNotes] = useState(false);
  const [showRotatePrompt, setShowRotatePrompt] = useState(false);
  const [showViewerRotatePrompt, setShowViewerRotatePrompt] = useState(false);
  const [viewerSessionId, setViewerSessionId] = useState(null);
  const heartbeatIntervalRef = useRef(null);

  // Track viewer session for analytics
  useEffect(() => {
    const startViewerSession = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/viewer-sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionId,
            ip_address: 'client', // Will be captured on backend
            user_agent: navigator.userAgent
          })
        });
        
        const data = await response.json();
        setViewerSessionId(data.viewer_session_id);
        
        // Start heartbeat to track active viewing
        heartbeatIntervalRef.current = setInterval(async () => {
          try {
            await fetch(`${BACKEND_URL}/api/viewer-sessions/${data.viewer_session_id}/heartbeat`, {
              method: 'PUT'
            });
          } catch (err) {
            console.error('Heartbeat failed:', err);
          }
        }, 30000); // Every 30 seconds
        
      } catch (error) {
        console.error('Failed to start viewer session:', error);
      }
    };
    
    if (sessionId) {
      startViewerSession();
    }
    
    // End viewer session when component unmounts
    return () => {
      if (viewerSessionId) {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        fetch(`${BACKEND_URL}/api/viewer-sessions/${viewerSessionId}/end`, {
          method: 'PUT'
        }).catch(console.error);
      }
      
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [sessionId]);

  // Debug: Track isSlideFullscreen changes
  useEffect(() => {
    console.log('🎬 isSlideFullscreen changed:', isSlideFullscreen);
  }, [isSlideFullscreen]);

  // Debug: Track modal state changes
  useEffect(() => {
    console.log('📋 showQRModal changed:', showQRModal);
  }, [showQRModal]);

  useEffect(() => {
    console.log('📝 showSpeakerNotes changed:', showSpeakerNotes);
  }, [showSpeakerNotes]);

  const checkViewerOrientation = () => {
    // Check if in landscape on mobile in normal viewer mode (not slideshow)
    const isLandscape = window.innerWidth > window.innerHeight;
    // Use the smaller dimension to detect mobile regardless of orientation
    const isMobile = Math.min(window.innerWidth, window.innerHeight) < 768;
    
    // Debug logging
    console.log('Viewer Orientation Check:', {
      isSlideFullscreen,
      isLandscape,
      isMobile,
      width: window.innerWidth,
      height: window.innerHeight,
      minDimension: Math.min(window.innerWidth, window.innerHeight)
    });
    
    // Show blocking overlay when in wrong orientation
    if (!isSlideFullscreen && isLandscape && isMobile) {
      console.log('🔒 Showing viewer rotate prompt');
      setShowViewerRotatePrompt(true);
    } else {
      setShowViewerRotatePrompt(false);
    }
  };

  const checkOrientation = (inSlideshowMode = null) => {
    // Check if device is in correct orientation
    const isLandscape = window.innerWidth > window.innerHeight;
    const isPortrait = window.innerHeight > window.innerWidth;
    
    // Use parameter if provided, otherwise use state
    const isSlideshowActive = inSlideshowMode !== null ? inSlideshowMode : isSlideFullscreen;
    
    if (isSlideshowActive && isPortrait) {
      // In slideshow mode but device is portrait - show rotate prompt
      setShowRotatePrompt(true);
    } else if (!isSlideshowActive && isLandscape) {
      // In normal viewer but device is landscape - could show prompt (optional)
      setShowRotatePrompt(false);
    } else {
      setShowRotatePrompt(false);
    }
  };

  const exitSlideshow = async () => {
    try {
      console.log('🚪 exitSlideshow called');
      
      // Try to exit fullscreen if active
      const isCurrentlyFullscreen = 
        document.fullscreenElement || 
        document.webkitFullscreenElement || 
        document.mozFullScreenElement || 
        document.msFullscreenElement;

      console.log('📺 Fullscreen status:', { isCurrentlyFullscreen: !!isCurrentlyFullscreen });

      if (isCurrentlyFullscreen) {
        if (document.exitFullscreen) {
          console.log('Calling document.exitFullscreen()');
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          console.log('Calling document.webkitExitFullscreen()');
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          console.log('Calling document.mozCancelFullScreen()');
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          console.log('Calling document.msExitFullscreen()');
          document.msExitFullscreen();
        }
      }
      
      // Always update state regardless of fullscreen API
      console.log('✅ Updating state: isSlideFullscreen = false');
      setIsSlideFullscreen(false);
      setShowRotatePrompt(false);
    } catch (error) {
      console.error('❌ Exit slideshow error:', error);
      // Force state update even if error
      setIsSlideFullscreen(false);
      setShowRotatePrompt(false);
    }
  };

  const toggleSlideFullscreen = async () => {
    try {
      // Check if we're currently in slideshow mode (by state, not just fullscreen API)
      if (isSlideFullscreen) {
        // Exit slideshow
        await exitSlideshow();
      } else {
        // Check orientation IMMEDIATELY when entering slideshow
        const isPortrait = window.innerHeight > window.innerWidth;
        
        // Enter fullscreen - set state first to ensure the element is rendered
        setIsSlideFullscreen(true);
        
        // Show rotate prompt immediately if in portrait on mobile
        if (isPortrait) {
          setShowRotatePrompt(true);
        }
        
        // Small delay to ensure the DOM is updated with the fullscreen element
        setTimeout(() => {
          if (fullscreenRef.current) {
            const element = fullscreenRef.current;
            
            // Try different fullscreen APIs based on browser support
            // For mobile browsers, try to request landscape orientation
            const fullscreenOptions = { 
              navigationUI: 'hide'
            };
            
            if (element.requestFullscreen) {
              element.requestFullscreen(fullscreenOptions).catch((error) => {
                console.log('Standard fullscreen not supported, trying alternatives:', error);
              });
            } else if (element.webkitRequestFullscreen) {
              // Safari/iOS
              element.webkitRequestFullscreen();
            } else if (element.mozRequestFullScreen) {
              // Firefox
              element.mozRequestFullScreen();
            } else if (element.msRequestFullscreen) {
              // IE/Edge
              element.msRequestFullscreen();
            } else {
              console.log('Fullscreen API not supported, using CSS fullscreen');
            }
          }
        }, 100);
      }
    } catch (error) {
      console.error('Toggle fullscreen error:', error);
      // Fallback to toggling state
      if (isSlideFullscreen) {
        setIsSlideFullscreen(false);
        setShowRotatePrompt(false);
      } else {
        setIsSlideFullscreen(true);
        // Check orientation for fallback too
        const isPortrait = window.innerHeight > window.innerWidth;
        if (isPortrait) {
          setShowRotatePrompt(true);
        }
      }
    }
  };

  // Listen for fullscreen changes (user pressing ESC)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement || 
        document.webkitFullscreenElement || 
        document.mozFullScreenElement || 
        document.msFullscreenElement
      );
      
      // If exiting fullscreen, hide rotate prompt
      if (!isFullscreen) {
        setShowRotatePrompt(false);
      }
      
      setIsSlideFullscreen(isFullscreen);
    };

    // Listen to all vendor-prefixed fullscreen change events
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [isSlideFullscreen]);

  // Auto-hide controls in slideshow mode
  useEffect(() => {
    if (!isSlideFullscreen) {
      setShowSlideshowControls(true);
      return;
    }

    const resetTimer = () => {
      setShowSlideshowControls(true);
      if (slideshowControlsTimer.current) {
        clearTimeout(slideshowControlsTimer.current);
      }
      slideshowControlsTimer.current = setTimeout(() => {
        setShowSlideshowControls(false);
      }, 3000);
    };

    const handleActivity = () => {
      resetTimer();
    };

    // Initial timer
    resetTimer();

    // Listen for mouse move and touch
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('click', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('click', handleActivity);
      if (slideshowControlsTimer.current) {
        clearTimeout(slideshowControlsTimer.current);
      }
    };
  }, [isSlideFullscreen]);

  // Keyboard navigation in slideshow mode
  useEffect(() => {
    if (!isSlideFullscreen) return;

    const handleKeyDown = (e) => {
      // Only allow manual navigation if session is NOT live (offline mode)
      // In live mode, viewer follows presenter
      const isOffline = session && !session.is_live;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ': // Spacebar
          e.preventDefault();
          if (isOffline && currentSlideIndex < presentation.slides.length - 1) {
            goToNext();
          }
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          if (isOffline && currentSlideIndex > 0) {
            goToPrevious();
          }
          break;
        case 'Escape':
          e.preventDefault();
          exitSlideshow();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSlideFullscreen, currentSlideIndex, presentation, session]);

  const goToPrevious = () => {
    if (currentSlideIndex > 0) {
      const newIndex = currentSlideIndex - 1;
      setCurrentSlideIndex(newIndex);
      setCurrentSlide(presentation.slides[newIndex]);
    }
  };

  const goToNext = () => {
    if (presentation && currentSlideIndex < presentation.slides.length - 1) {
      const newIndex = currentSlideIndex + 1;
      setCurrentSlideIndex(newIndex);
      setCurrentSlide(presentation.slides[newIndex]);
    }
  };

  // Handler for QR button in slideshow - exits slideshow first on desktop
  const handleShowQRInSlideshow = async () => {
    console.log('🔍 QR Button Clicked', {
      isSlideFullscreen,
      width: window.innerWidth,
      height: window.innerHeight,
      minDimension: Math.min(window.innerWidth, window.innerHeight)
    });
    
    if (isSlideFullscreen) {
      // Check if it's desktop (not mobile)
      const isMobile = Math.min(window.innerWidth, window.innerHeight) < 768;
      
      console.log('📱 Device detection:', { isMobile, isSlideFullscreen });
      
      if (!isMobile) {
        // Desktop: Exit slideshow first, then show QR modal
        console.log('🖥️ Desktop detected - Exiting slideshow...');
        await exitSlideshow();
        console.log('🕐 Waiting 300ms before showing QR modal...');
        // Small delay to ensure slideshow has exited
        setTimeout(() => {
          console.log('✅ NOW calling setShowQRModal(true)');
          setShowQRModal(true);
          console.log('✅ setShowQRModal called - modal should appear');
        }, 300);
      } else {
        // Mobile: Just show QR modal over slideshow
        console.log('📱 Mobile detected - Showing QR over slideshow');
        setShowQRModal(true);
      }
    } else {
      // Not in slideshow, just show QR modal
      console.log('ℹ️ Not in slideshow - Showing QR modal');
      setShowQRModal(true);
    }
  };

  // Handler for Notes button in slideshow - exits slideshow first on desktop
  const handleShowNotesInSlideshow = async () => {
    console.log('🔍 Notes Button Clicked', {
      isSlideFullscreen,
      width: window.innerWidth,
      height: window.innerHeight,
      minDimension: Math.min(window.innerWidth, window.innerHeight)
    });
    
    if (isSlideFullscreen) {
      // Check if it's desktop (not mobile)
      const isMobile = Math.min(window.innerWidth, window.innerHeight) < 768;
      
      console.log('📱 Device detection:', { isMobile, isSlideFullscreen });
      
      if (!isMobile) {
        // Desktop: Exit slideshow first, then show notes
        console.log('🖥️ Desktop detected - Exiting slideshow...');
        await exitSlideshow();
        console.log('🕐 Waiting 300ms before showing notes...');
        // Small delay to ensure slideshow has exited
        setTimeout(() => {
          console.log('✅ NOW calling setShowSpeakerNotes(true)');
          setShowSpeakerNotes(true);
          console.log('✅ setShowSpeakerNotes called - panel should appear');
        }, 300);
      } else {
        // Mobile: Just show notes over slideshow
        setShowSpeakerNotes(true);
      }
    } else {
      // Not in slideshow, just show notes
      setShowSpeakerNotes(true);
    }
  };

  // Listen for orientation changes with iOS Safari support
  useEffect(() => {
    const handleOrientationChange = () => {
      // Small delay to ensure window dimensions are updated on iOS
      setTimeout(() => {
        checkOrientation();
        checkViewerOrientation();
      }, 100);
    };
    
    // Check orientation on mount and on changes
    checkOrientation();
    checkViewerOrientation();
    
    // Listen for multiple orientation change events (iOS Safari compatibility)
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    
    // Also use matchMedia for more reliable detection on iOS
    const portraitQuery = window.matchMedia('(orientation: portrait)');
    const landscapeQuery = window.matchMedia('(orientation: landscape)');
    
    const mediaChangeHandler = () => {
      handleOrientationChange();
    };
    
    // Modern browsers
    if (portraitQuery.addEventListener) {
      portraitQuery.addEventListener('change', mediaChangeHandler);
      landscapeQuery.addEventListener('change', mediaChangeHandler);
    } else if (portraitQuery.addListener) {
      // Older browsers
      portraitQuery.addListener(mediaChangeHandler);
      landscapeQuery.addListener(mediaChangeHandler);
    }
    
    // Fallback: Continuous monitoring for iOS Safari (runs every 500ms)
    // This ensures we catch orientation changes even if events don't fire
    const orientationCheckInterval = setInterval(() => {
      checkViewerOrientation();
    }, 500);
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
      clearInterval(orientationCheckInterval);
      
      if (portraitQuery.removeEventListener) {
        portraitQuery.removeEventListener('change', mediaChangeHandler);
        landscapeQuery.removeEventListener('change', mediaChangeHandler);
      } else if (portraitQuery.removeListener) {
        portraitQuery.removeListener(mediaChangeHandler);
        landscapeQuery.removeListener(mediaChangeHandler);
      }
    };
  }, [isSlideFullscreen]); // Re-run when slideshow mode changes

  useEffect(() => {
    // Load session and presentation
    const loadSession = async () => {
      try {
        const data = await getSession(sessionId);
        setSession(data.session);
        setPresentation(data.presentation);
        setCurrentSlideIndex(data.session.current_slide);
        setCurrentSlide(data.presentation.slides[data.session.current_slide]);
        setLoading(false);
      } catch (error) {
        console.error('Error loading session:', error);
        setLoading(false);
      }
    };

    loadSession();
  }, [sessionId]);

  useEffect(() => {
    if (!presentation) return;

    let pollInterval = null;
    let isWebSocketWorking = false;

    // Connect to WebSocket for real-time updates
    const setupWebSocket = async () => {
      try {
        await websocketService.connect();
        await websocketService.joinSession(sessionId);
        console.log('ViewerMain: WebSocket setup complete');
        isWebSocketWorking = true;
      } catch (error) {
        console.error('ViewerMain: WebSocket setup failed, using polling fallback:', error);
        isWebSocketWorking = false;
      }
    };

    setupWebSocket();

    // Polling fallback for when WebSocket fails (Safari incognito, etc.)
    const startPollingFallback = () => {
      pollInterval = setInterval(async () => {
        if (isWebSocketWorking) {
          // WebSocket is working, no need to poll
          return;
        }
        
        try {
          const data = await getSession(sessionId);
          
          setSession(prevSession => {
            // Check if session state changed
            const slideChanged = prevSession.current_slide !== data.session.current_slide;
            const liveChanged = prevSession.is_live !== data.session.is_live;
            
            // If live and slide changed, sync
            if (data.session.is_live && slideChanged) {
              console.log('Polling: Syncing to slide', data.session.current_slide);
              setCurrentSlideIndex(data.session.current_slide);
              setCurrentSlide(data.presentation.slides[data.session.current_slide]);
            }
            
            // If just went live, sync immediately
            if (!prevSession.is_live && data.session.is_live) {
              console.log('Polling: Went live, syncing');
              setCurrentSlideIndex(data.session.current_slide);
              setCurrentSlide(data.presentation.slides[data.session.current_slide]);
            }
            
            return data.session;
          });
          
          // Update presentation if depth notes changed
          setPresentation(prevPres => {
            const hasChanges = JSON.stringify(prevPres.slides) !== JSON.stringify(data.presentation.slides);
            if (hasChanges) {
              // Update current slide if it's the same index
              setCurrentSlide(prevSlide => {
                const currentIndex = prevPres.slides.findIndex(s => s.id === prevSlide.id);
                return data.presentation.slides[currentIndex] || prevSlide;
              });
              return data.presentation;
            }
            return prevPres;
          });
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 200); // Poll every 200ms (0.2 seconds)
    };

    // Start polling fallback after 3 seconds (give WebSocket time to connect)
    const fallbackTimeout = setTimeout(() => {
      if (!websocketService.isSocketConnected()) {
        console.log('ViewerMain: Starting polling fallback');
        startPollingFallback();
      }
    }, 3000);

    // Listen for slide changes - only sync when live
    const handleSlideChange = (data) => {
      if (data.session_id === sessionId) {
        console.log('WebSocket: Slide change received:', data);
        isWebSocketWorking = true;
        
        setSession(prevSession => {
          // Only sync viewer's slide if presentation is live
          if (prevSession.is_live) {
            console.log('WebSocket: Syncing to slide:', data.current_slide);
            setCurrentSlideIndex(data.current_slide);
            
            setPresentation(prevPres => {
              if (prevPres && prevPres.slides[data.current_slide]) {
                setCurrentSlide(prevPres.slides[data.current_slide]);
              }
              return prevPres;
            });
          }
          
          return { ...prevSession, current_slide: data.current_slide };
        });
      }
    };

    // Listen for live status changes
    const handleLiveStatusChange = (data) => {
      if (data.session_id === sessionId) {
        console.log('WebSocket: Live status change:', data.is_live);
        isWebSocketWorking = true;
        
        setSession(prevSession => {
          // When going live, sync to presenter's current slide
          if (data.is_live && prevSession.current_slide !== undefined) {
            console.log('WebSocket: Going live, syncing to slide:', prevSession.current_slide);
            setCurrentSlideIndex(prevSession.current_slide);
            
            setPresentation(prevPres => {
              if (prevPres && prevPres.slides[prevSession.current_slide]) {
                setCurrentSlide(prevPres.slides[prevSession.current_slide]);
              }
              return prevPres;
            });
          }
          
          return { ...prevSession, is_live: data.is_live };
        });
      }
    };

    // Listen for depth notes updates
    const handleDepthNotesUpdate = (data) => {
      isWebSocketWorking = true;
      
      setPresentation(prevPres => {
        if (!prevPres) return prevPres;
        
        const updatedSlides = prevPres.slides.map(slide =>
          slide.id === data.slide_id ? { ...slide, depth_notes: data.depth_notes } : slide
        );
        
        setCurrentSlide(prevSlide => 
          prevSlide && prevSlide.id === data.slide_id 
            ? { ...prevSlide, depth_notes: data.depth_notes } 
            : prevSlide
        );
        
        return { ...prevPres, slides: updatedSlides };
      });
    };

    websocketService.onSlideChange(handleSlideChange);
    websocketService.onLiveStatusChange(handleLiveStatusChange);
    websocketService.onDepthNotesUpdate(handleDepthNotesUpdate);

    return () => {
      clearTimeout(fallbackTimeout);
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      websocketService.removeListener('slide_change');
      websocketService.removeListener('live_status_change');
      websocketService.removeListener('depth_notes_update');
      websocketService.leaveSession(sessionId);
    };
  }, [sessionId, presentation]);

  const goToDepthView = () => {
    navigate(`/view/${sessionId}/depth`);
  };

  const viewerLink = `${window.location.origin}/view/${sessionId}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600">Loading presentation...</p>
      </div>
    );
  }

  if (!session || !presentation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600">Presentation not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {!isSlideFullscreen && (
        <div className="container mx-auto px-6 py-8 max-w-5xl">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                {presentation.title}
              </h1>
              <p className="text-slate-600 text-sm mt-1">Viewer Mode</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={session.is_live ? "default" : "secondary"} className="text-sm px-3 py-1">
                {session.is_live ? 'Live' : 'Offline'}
              </Badge>
              <Button
                onClick={() => setShowQRModal(true)}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <QrCode className="w-4 h-4" />
              </Button>
              <Button
                onClick={toggleSlideFullscreen}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Maximize className="w-4 h-4" />
                Slideshow
              </Button>
            </div>
          </div>

          {/* Main Slide Display */}
          <Card className="border-slate-200 shadow-xl mb-6 relative">
            <CardContent className="p-0">
              <div 
                className="rounded-lg p-4 sm:p-6 md:p-8 overflow-y-auto" 
                style={{ 
                  minHeight: '300px',
                  maxHeight: '75vh', 
                  background: currentSlide?.background?.type === 'color' 
                    ? currentSlide.background.value 
                    : currentSlide?.background?.type === 'gradient'
                    ? currentSlide.background.value
                    : currentSlide?.background?.type === 'image'
                    ? `url(${currentSlide.background.value}) center/cover`
                    : '#ffffff'
                }}
              >
                {/* Show PDF page if available */}
                {presentation.pdf_data && currentSlide?.pdf_page_number > 0 ? (
                  <div className="w-full h-full flex items-center justify-center min-h-[300px]">
                    <PDFSlideViewer 
                      key={`viewer-slide-${currentSlideIndex}-page-${currentSlide.pdf_page_number}`}
                      pdfData={presentation.pdf_data} 
                      pageNumber={currentSlide.pdf_page_number}
                      maxWidth={1200}
                      maintainAspectRatio={false}
                    />
                  </div>
                ) : (
                  <div className="w-full">
                    <h2 
                      className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 break-words"
                      style={{ overflowWrap: 'break-word', wordWrap: 'break-word' }}
                      dangerouslySetInnerHTML={{ __html: currentSlide?.title || '' }}
                    />
                    <div 
                      className="text-base sm:text-lg md:text-xl leading-relaxed break-words"
                      style={{ overflowWrap: 'break-word', wordWrap: 'break-word' }}
                      dangerouslySetInnerHTML={{ __html: currentSlide?.content || '' }}
                    />
                  </div>
                )}
              </div>
            </CardContent>
            
            {/* Navigation Controls - Only show when offline */}
            {!session.is_live && (
              <>
                <Button
                  onClick={goToPrevious}
                  disabled={currentSlideIndex === 0}
                  size="sm"
                  variant="outline"
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 rounded-full h-12 w-12 p-0 shadow-lg"
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  onClick={goToNext}
                  disabled={currentSlideIndex === presentation.slides.length - 1}
                  size="sm"
                  variant="outline"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 rounded-full h-12 w-12 p-0 shadow-lg"
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </>
            )}
          </Card>

          {/* Slide Counter - Enhanced for offline mode */}
          <div className="mt-8 text-center">
            {!session.is_live && (
              <p className="text-blue-600 text-sm font-medium mb-2">
                🔓 Browse Mode: Use arrow buttons to navigate
              </p>
            )}
            <p className="text-slate-500 text-sm">
              Slide {currentSlideIndex + 1} of {presentation.slides.length}
              {!session.is_live && session.current_slide !== currentSlideIndex && (
                <span className="ml-2 text-slate-400">
                  (Presenter on slide {session.current_slide + 1})
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Fullscreen Slideshow Mode */}
      {isSlideFullscreen && (
        <div 
          ref={fullscreenRef}
          className="fixed inset-0 z-40 flex items-center justify-center"
          style={{
            width: '100vw',
            height: '100vh',
            background: currentSlide?.background?.type === 'color' 
              ? currentSlide.background.value 
              : currentSlide?.background?.type === 'gradient'
              ? currentSlide.background.value
              : currentSlide?.background?.type === 'image'
              ? `url(${currentSlide.background.value}) center/cover`
              : '#000000'
          }}
        >
          {/* Slide Content - Height-first sizing with padding for desktop */}
          <div className="absolute inset-0 flex items-center justify-center p-4 md:p-8 lg:p-16">
            {/* Show PDF page if available */}
            {presentation.pdf_data && currentSlide?.pdf_page_number > 0 ? (
              <div className="w-full h-full flex items-center justify-center" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
                <PDFSlideViewer 
                  key={`viewer-fullscreen-slide-${currentSlideIndex}-page-${currentSlide.pdf_page_number}`}
                  pdfData={presentation.pdf_data} 
                  pageNumber={currentSlide.pdf_page_number}
                  height={window.innerHeight - 32}
                  maintainAspectRatio={true}
                />
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center overflow-y-auto px-4 py-8 md:px-16" style={{ maxHeight: '100%' }}>
                <div className="w-full max-w-6xl text-center">
                  <h2 
                    className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold mb-6 md:mb-12 leading-tight break-words"
                    style={{ overflowWrap: 'break-word', wordWrap: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: currentSlide?.title || '' }}
                  />
                  <div 
                    className="text-lg sm:text-xl md:text-3xl lg:text-4xl leading-relaxed break-words"
                    style={{ overflowWrap: 'break-word', wordWrap: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: currentSlide?.content || '' }}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Navigation Controls - Auto-hide (only in offline mode) */}
          {session && !session.is_live && (
            <>
              <div className={`absolute left-8 top-1/2 transform -translate-y-1/2 transition-opacity duration-300 z-50 ${
                showSlideshowControls ? 'opacity-100' : 'opacity-0'
              }`}>
                <Button
                  onClick={goToPrevious}
                  disabled={currentSlideIndex === 0}
                  size="lg"
                  variant="ghost"
                  className="rounded-full h-16 w-16 p-0 bg-white/90 hover:bg-white text-slate-900 shadow-lg disabled:opacity-20"
                >
                  <ChevronLeft className="w-8 h-8" />
                </Button>
              </div>
              
              <div className={`absolute right-8 top-1/2 transform -translate-y-1/2 transition-opacity duration-300 z-50 ${
                showSlideshowControls ? 'opacity-100' : 'opacity-0'
              }`}>
                <Button
                  onClick={goToNext}
                  disabled={currentSlideIndex === presentation.slides.length - 1}
                  size="lg"
                  variant="ghost"
                  className="rounded-full h-16 w-16 p-0 bg-white/90 hover:bg-white text-slate-900 shadow-lg disabled:opacity-20"
                >
                  <ChevronRight className="w-8 h-8" />
                </Button>
              </div>
            </>
          )}
          
          {/* Live Mode Indicator */}
          {session && session.is_live && (
            <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-opacity duration-300 ${
              showSlideshowControls ? 'opacity-100' : 'opacity-0'
            }`}>
              <div className="bg-red-600/20 backdrop-blur-sm px-6 py-3 rounded-full border border-red-500/50">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <p className="text-white text-sm font-medium">LIVE - Following Presenter</p>
                </div>
              </div>
            </div>
          )}

          {/* Top Controls - Auto-hide */}
          <div className={`absolute top-6 right-6 flex items-center gap-3 transition-opacity duration-300 z-50 ${
            showSlideshowControls ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}>
            <Button
              onClick={(e) => {
                console.log('🎯 NOTES BUTTON CLICKED - Event received!', e);
                console.log('⚡ TESTING: Just setting state directly without exit');
                setShowSpeakerNotes(true);
                // handleShowNotesInSlideshow();
              }}
              variant="ghost"
              size="sm"
              className="gap-2 bg-white/90 hover:bg-white text-slate-900 backdrop-blur-sm shadow-lg"
            >
              <MessageCircle className="w-4 h-4" />
              Notes
            </Button>
            <Button
              onClick={(e) => {
                console.log('🎯 SHARE BUTTON CLICKED - Event received!', e);
                console.log('⚡ TESTING: Just setting state directly without exit');
                setShowQRModal(true);
                // handleShowQRInSlideshow();
              }}
              variant="ghost"
              size="sm"
              className="gap-2 bg-white/90 hover:bg-white text-slate-900 backdrop-blur-sm shadow-lg"
            >
              <QrCode className="w-4 h-4" />
              Share
            </Button>
            <Button
              onClick={toggleSlideFullscreen}
              variant="ghost"
              size="sm"
              className="gap-2 bg-white/90 hover:bg-white text-slate-900 backdrop-blur-sm shadow-lg"
            >
              <Minimize className="w-4 h-4" />
              Exit
            </Button>
          </div>

          {/* Slide Counter - Auto-hide */}
          <div className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-3 bg-white/90 backdrop-blur-md rounded-full transition-opacity duration-300 shadow-lg ${
            showSlideshowControls ? 'opacity-100' : 'opacity-0'
          }`}>
            <p className="text-slate-900 text-sm font-medium">
              {currentSlideIndex + 1} / {presentation.slides.length}
            </p>
          </div>
        </div>
      )}
      
      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        url={viewerLink}
        title="Share Presentation"
      />
      
      {/* Swipe Up Panel for Speaker Notes */}
      <SwipeUpPanel 
        slide={currentSlide}
        presentation={presentation}
        isFullscreen={isSlideFullscreen}
        externalOpen={showSpeakerNotes}
        onClose={() => setShowSpeakerNotes(false)}
      />
      
      {/* Rotate Device Overlay for Slideshow (Landscape Only) - Minimalist Design */}
      {showRotatePrompt && isSlideFullscreen && (
        <div className="fixed inset-0 bg-black z-[200] flex flex-col items-center justify-center p-8">
          <div className="text-center max-w-md">
            {/* Simple Phone Icon with Rotation Animation - Landscape */}
            <div className="mb-8">
              <div className="relative w-24 h-24 mx-auto">
                <svg 
                  className="w-32 h-16 text-white animate-[wiggle_1s_ease-in-out_infinite]" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <rect x="2" y="5" width="20" height="14" rx="2" ry="2" />
                  <line x1="18" y1="12" x2="18" y2="12" strokeWidth={2} strokeLinecap="round" />
                </svg>
                {/* Curved Arrow */}
                <svg 
                  className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-12 h-12 text-white/60 animate-pulse" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" 
                  />
                </svg>
              </div>
            </div>
            
            {/* Minimalist Text */}
            <h2 className="text-xl font-semibold text-white mb-3 mt-8">
              Rotate to Landscape
            </h2>
            <p className="text-white/70 text-sm leading-relaxed">
              Please rotate your device to landscape mode for the best slideshow experience.
            </p>
          </div>
        </div>
      )}
      
      {/* Rotate Device Overlay for Viewer Mode (Portrait Only) - Minimalist Design */}
      {showViewerRotatePrompt && !isSlideFullscreen && (
        <div className="fixed inset-0 bg-white z-[200] flex flex-col items-center justify-center p-8">
          <div className="text-center max-w-md">
            {/* Simple Phone Icon with Rotation Animation */}
            <div className="mb-8">
              <div className="relative w-24 h-24 mx-auto">
                <svg 
                  className="w-24 h-24 text-slate-800 animate-[wiggle_1s_ease-in-out_infinite]" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                >
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                  <line x1="12" y1="18" x2="12" y2="18" strokeWidth={2} strokeLinecap="round" />
                </svg>
                {/* Curved Arrow */}
                <svg 
                  className="absolute -right-8 top-1/2 -translate-y-1/2 w-12 h-12 text-slate-400 animate-pulse" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" 
                  />
                </svg>
              </div>
            </div>
            
            {/* Minimalist Text */}
            <h2 className="text-xl font-semibold text-slate-900 mb-3">
              Rotate to Portrait
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              Please rotate your device to portrait mode for the best viewing experience.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewerMain;
